import React from 'react';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ChartContainer from './ChartContainer';
import { useTheme } from '../../hooks/useTheme';
import { getChartColors } from '../../utils/chartColors';

interface PieChartData {
  label: string;
  value: number;
  color: string;
  percentage: number;
}

interface PieChartProps {
  data: PieChartData[];
  title?: string;
  showLegend?: boolean;
  showCenterValue?: boolean;
  centerValue?: string | number;
  className?: string;
  height?: string;
}

const PieChartComponent: React.FC<PieChartProps> = ({
  data,
  title = "Pie Chart",
  showLegend = true,
  showCenterValue = true,
  centerValue,
  className = '',
  height = 'h-64'
}) => {
  const { isDark } = useTheme();
  const colors = getChartColors(isDark);
  const total = data.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <ChartContainer title={title} className={className} height={height}>
        <div className="flex items-center justify-center h-full text-neutral-500 dark:text-neutral-400">
          No data available
        </div>
      </ChartContainer>
    );
  }

  const chartData = data.map(item => ({
    name: item.label,
    value: item.value,
    fill: item.color
  }));

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (!showCenterValue) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text x={x} y={y} fill="white" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central">
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <ChartContainer title={title} className={className} height={height}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomizedLabel}
            outerRadius={80}
            fill="#8884d8"
            dataKey="value"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.fill} />
            ))}
          </Pie>
          {showLegend && <Legend />}
          <Tooltip
            formatter={(value: number) => [value, 'Value']}
            contentStyle={{
              backgroundColor: colors.tooltip.background,
              border: `1px solid ${colors.tooltip.border}`,
              borderRadius: '8px',
              color: colors.tooltip.text
            }}
          />
        </PieChart>
      </ResponsiveContainer>
      {showCenterValue && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-lg font-bold text-neutral-900 dark:text-neutral-100">
            {centerValue || total}
          </span>
        </div>
      )}
    </ChartContainer>
  );
};

export default PieChartComponent;
